import { Deserializable } from '../../protocols/deserializable';
import { Orderable } from '../../protocols/orderable';
import { SectionMetadata } from './section-metadata';
import { DateUtils } from '../../../utils/date-utils';
import { UniquelyIdentifiable } from '../../protocols/uniquely-identifiable';
import { SectionType } from '../../../utils/section-type-definition';
import { SectionSortOption, SectionSortOptionProductInfo } from '../../../utils/section-sort-type';
import { SectionLayout } from '../../../utils/section-layout-type';
import { CardType } from '../../../utils/card-type-definition';
import { SaleLabelFormat } from '../../../utils/sale-label-format-type';
import { SectionColumnConfig } from './section-column-config';
import { SectionColumnConfigKey } from '../../enum/dto/section-column-config-key';
import { SectionColumnConfigKeyType } from '../../../utils/section-column-config-key-type';

export class Section implements Deserializable, Orderable, UniquelyIdentifiable {
  public configurationId: string;
  public id: string;
  public title: string;
  public subTitle: string;
  public autoUpdateGridColumns: boolean = false;
  public columnConfig: Map<SectionColumnConfigKey, SectionColumnConfig>;
  public dateCreated: number;
  public lastUpdated: number;
  public layoutType: SectionLayout;
  public metadata: SectionMetadata;
  public rowCount: number;
  public sorting: SectionSortOption;
  public secondarySorting: SectionSortOption;
  public sectionType: SectionType;
  public showZeroStockItems: boolean;
  public smartFilterIds: string[];
  public priority: number;
  public productIds: string[];
  public enabledVariantIds: string[];
  public masterSectionId: string;
  public variantBadgeIdsMap: Map<string, string[]>;
  public customLabelMap: Map<string, string>;
  public lastSmartFilterSync: number;
  public saleLabelFormat: SaleLabelFormat;
  public cardType: CardType;
  // Cache
  public cachedTime: number;

  static newSection(): Section {
    const s = new Section();
    s.title = '';
    s.rowCount = 0;
    s.sectionType = null;
    s.layoutType = SectionLayout.List;
    s.sorting = SectionSortOptionProductInfo.PackagedQuantityDesc;
    return s;
  }

  public onDeserialize() {
    const Deserialize = window?.injector?.Deserialize;
    // New array instances
    if (!this.columnConfig) {
      this.columnConfig = new Map<SectionColumnConfigKey, SectionColumnConfig>();
    } else if (!(this.columnConfig instanceof Map)) {
      this.columnConfig = Deserialize?.mapOf(SectionColumnConfig, this.columnConfig);
    } else {
      this.columnConfig = new Map<SectionColumnConfigKey, SectionColumnConfig>(this.columnConfig);
    }

    this.deserializeColumnConfig();
    this.metadata = Deserialize?.instanceOf(SectionMetadata, this.metadata);
    this.smartFilterIds = Array.from(this.smartFilterIds || []);
    this.productIds = Array.from(this.productIds || []);
    this.enabledVariantIds = Array.from(this.enabledVariantIds || []);
    this.variantBadgeIdsMap = Deserialize.genericArrayMap(this.variantBadgeIdsMap);

    if (!this.customLabelMap) {
      this.customLabelMap = new Map<string, string>();
    } else if (!(this.customLabelMap instanceof Map)) {
      this.customLabelMap = Deserialize.genericMap(this.customLabelMap);
    } else {
      this.customLabelMap = new Map<string, string>(this.customLabelMap);
    }
    this.transformFalsyRowCountToNull();
  }

  private deserializeColumnConfig() {
    if (!this.columnConfig) {
      this.columnConfig = new Map<SectionColumnConfigKey, SectionColumnConfig>();
    } else if (!(this.columnConfig instanceof Map)) {
      this.columnConfig = window?.injector?.Deserialize?.mapOf(SectionColumnConfig, this.columnConfig);
    } else {
      this.columnConfig = new Map<SectionColumnConfigKey, SectionColumnConfig>(this.columnConfig);
    }
    SectionColumnConfigKeyType.getAllKeys()?.forEach(key => {
      if (!this.columnConfig?.get(key)) {
        this.columnConfig.set(key, new SectionColumnConfig());
      }
    });
  }

  private transformFalsyRowCountToNull() {
    if (!this.rowCount) this.rowCount = null;
  }

  public setEmptyValues() {
    this.metadata = this.metadata
      ? window.injector.Deserialize.instanceOf(SectionMetadata, this.metadata)
      : new SectionMetadata();
    if (!this.variantBadgeIdsMap) {
      this.variantBadgeIdsMap = new Map<string, string[]>();
    }
  }

  public getFormattedSectionType(): string {
    switch (this.sectionType) {
      case SectionType.Image:
        return 'Image';
      case SectionType.Product:
        return 'Product';
      case SectionType.Title:
        return 'Title';
    }
  }

  getOrderValue(): number {
    return this.priority;
  }

  getOrderableTitle(): string {
    return this.title;
  }

  getOrderableUniqueId(): string {
    return this.id;
  }

  setOrderableValue(val: number) {
    this.priority = val;
  }

  static buildCacheKey(id: string): string {
    return `Section-${id}`;
  }

  cacheExpirySeconds(): number {
    return DateUtils.secondsInOneHour();
  }

  cacheKey(): string {
    return Section.buildCacheKey(this.id);
  }

  isExpired(): boolean {
    const expiresAt = this.cachedTime + this.cacheExpirySeconds();
    return DateUtils.currentTimestampInSeconds() > expiresAt;
  }

  getUniqueIdentifier(...opts: any): string {
    const id = this.id;
    const titleId = this.title ?? '';
    const subtitleId = this.subTitle ?? '';
    const zeroStockId = `${this.showZeroStockItems}`;
    const sortingId = this.sorting ?? '';
    const sectionTypeId = this.sectionType ?? '';
    const layoutTypeId = this.layoutType ?? '';
    const columnConfigMap: string[] = [];
    this.columnConfig?.forEach((val, key) => {
      const columnConfig = val?.getUniqueIdentifier();
      columnConfigMap.push(`${key}-${columnConfig}`);
    });
    const metadataId = this.metadata?.getUniqueIdentifier() ?? '';
    const rowCount = `${this.rowCount}`;
    const smartFilterIds = this.smartFilterIds.sort().join(',') ?? '';
    const priorityId = this.priority.toString() ?? '0';
    const masterSectionId = this.masterSectionId ?? '';
    const variantBadgeIds: string[] = [];
    this.variantBadgeIdsMap?.forEach((val, key) => {
      const vbId = val.sort().join(',') ?? '';
      variantBadgeIds.push(`${key}-${vbId}`);
    });
    const customLabelMap: string[] = [];
    this.customLabelMap.forEach((val, key) => {
      customLabelMap.push(`${key}-${val}`);
    });
    const variantBadgesId = variantBadgeIds.sort().join(',') ?? '';
    const lastFilterSync = `${this.lastSmartFilterSync}`;
    return `${id}
            -${titleId}
            -${subtitleId}
            -${zeroStockId}
            -${sortingId}
            -${sectionTypeId}
            -${columnConfigMap.sort().join(',')}
            -${layoutTypeId}
            -${metadataId}
            -${rowCount}
            -${smartFilterIds}
            -${priorityId}
            -${customLabelMap.sort().join(',')}
            -${masterSectionId}
            -${variantBadgesId}
            -${lastFilterSync}`;
  }

  public hasSmartFilters(): boolean {
    return this.smartFilterIds?.length > 0;
  }
}
